import { useCallback, useEffect, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import Modal from "@mui/material/Modal";
import { useAuth } from "@/auth/AuthProvider";
import {
  capacityKindLabel,
  getAccountCapacityHistory,
  type AccountProgramCapacity,
  type CapacityHistoryRow,
} from "@/lib/brokerAccounts";
import {
  formatBondCurrency,
  formatBondDate,
  formatPreapprovalMoney,
} from "@/lib/bondValueDisplay";
import { messageFor } from "@/lib/errors";
import {
  listCapacityProjects,
  type CapacityProject,
  type CapacityRequestView,
} from "@/lib/capacityRequest";
import {
  aggregateSegments,
  layoutScale,
  segmentAccessibleName,
  segmentActionLabel,
  segmentKindLabel,
  payApplicationLine,
  segmentNote,
  segmentWidthPercent,
  type AggregateSegment,
  type LooseCredit,
  type SegmentKind,
} from "@/lib/aggregateSegments";
import { bidResultFact } from "@/lib/bondStateModel";
import type { BondSubmission } from "@/lib/brokerBonds";
import { buttonClass } from "@/components/buttonStyles";
import { ReportBidResultDialog } from "@/components/ReportBidResultDialog";
import {
  PayApplicationDialog,
  type FiledPayApplication,
} from "@/components/surety-account/PayApplicationDialog";
import { estimatedReleaseLine } from "@/lib/payApplication";

// BRK-S186 — aggregate utilized on the Surety program panel, and the
// transaction-history drill-in (consuming PMS-S493, pinned at BRK-S280).
//
// Rendered ONLY for a non-null `capacity` block: absent (a PMS that predates
// PMS-S493) and null (no current program) both mean "say nothing", never
// "$0". Inside the block, a null `aggregateLimit` is a program with no
// aggregate limit, so there is nothing to be "of", no bar and no "available".
//
// BRK-S402 — on the account page (`perBond`) the bar is the wireframe's
// SEGMENTED bar: one segment per order holding capacity (`GET
// …/capacity-projects`), coloured bid vs P&P from the served `basis`, the
// pending capacity request's credit hatched, a legend, and a row per segment
// with its action — a bid opens "Report bid result" (`ReportBidResultDialog`),
// a P&P opens the pay application (`PayApplicationDialog`, BRK-S404). The
// figures the broker reads stay PMS's own (`aggregateUtilized`, `remaining`);
// the segments are only laid out from the served amounts. After a recorded
// bid result or a filed pay application the bar RE-READS the projects and asks
// the page to re-read the account (`onChanged`), so what it draws next is
// PMS's answer, not a local subtraction (NN #7).
//
// The history is what a broker may be told: when, what happened, which order,
// how much, and where utilization stood after. Never the ledger's actor, note
// or basis — PMS does not publish them, and `CapacityHistoryRow` has no home
// for them.

/** Rows per page of the drill-in; "Load more" appends the next. */
const HISTORY_PAGE_SIZE = 10;

const TITLE_ID = "capacity-history-title";

/** Percent of the aggregate held, clamped so an over-limit program fills the
 *  bar rather than overflowing it. */
function percentHeld(utilized: number, limit: number): number {
  if (limit <= 0) return 100;
  return Math.min(100, Math.max(0, Math.round((utilized / limit) * 100)));
}

/** Fill per kind. A data colour, not a brand role: the bid reads blue as in
 *  the wireframe, a P&P the portal's accent, an unadopted basis neutral. */
const KIND_FILL: Record<SegmentKind, string> = {
  bid: "bg-sky-600",
  pp: "bg-brk-accent",
  other: "bg-brk-dim",
};

/** The credit in review: the accent, hatched. */
const HATCH: CSSProperties = {
  backgroundImage:
    "repeating-linear-gradient(135deg, rgb(var(--brk-accent) / 0.35) 0 3px, rgb(var(--brk-accent)) 3px 6px)",
};

function Swatch({ kind }: { kind: SegmentKind | "credit" }) {
  return (
    <span
      aria-hidden="true"
      className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-sm ${kind === "credit" ? "" : KIND_FILL[kind]}`}
      style={kind === "credit" ? HATCH : undefined}
    />
  );
}

export function ProgramUtilization({
  accountId,
  capacity,
  capacityRequest = null,
  perBond = false,
  onChanged,
}: {
  accountId: string;
  capacity: AccountProgramCapacity;
  /** The latest capacity request, already read — its pending credit is the
   *  hatched segment. */
  capacityRequest?: CapacityRequestView | null;
  /** Draw a segment and a row per order holding capacity, with actions (the
   *  account page). Off, the bar is one fill, as BRK-S186 drew it. */
  perBond?: boolean;
  /** Called after PMS recorded a change to what holds capacity, so the owner
   *  re-reads the account's figures. */
  onChanged?: () => void;
}) {
  const { authedRequest } = useAuth();
  const [open, setOpen] = useState(false);
  const { aggregateUtilized, aggregateLimit, remaining } = capacity;

  const [projects, setProjects] = useState<CapacityProject[] | null>(null);
  const [projectsError, setProjectsError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [bidFor, setBidFor] = useState<AggregateSegment | null>(null);
  // The P&P order the pay application opened on; undefined while closed.
  const [payAppFor, setPayAppFor] = useState<string | undefined>(undefined);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!perBond) return;
    let cancelled = false;
    setProjectsError(null);
    (async () => {
      try {
        const rows = await listCapacityProjects(authedRequest, accountId);
        if (!cancelled) setProjects(rows);
      } catch (err) {
        // Handled, never a throw — and never a false "nothing held" (NN #7).
        if (!cancelled) setProjectsError(messageFor(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [perBond, authedRequest, accountId, attempt]);

  function onRecorded(segment: AggregateSegment, order: BondSubmission) {
    setBidFor(null);
    // Worded from what PMS answered, not from what was sent.
    const fact = bidResultFact(order);
    setNotice(
      fact === "low_bidder"
        ? `${segment.orderNumber}: the client was the low bidder. The bid stays on the aggregate until the performance and payment bond is issued.`
        : fact === "lost"
          ? `${segment.orderNumber}: recorded as not the low bidder.`
          : `${segment.orderNumber}: your bid result is recorded.`,
    );
    setAttempt((n) => n + 1);
    onChanged?.();
  }

  function onPayAppFiled(filed: FiledPayApplication) {
    setPayAppFor(undefined);
    // From the 2xx: the request PMS accepted, and PMS's estimate if it sent one.
    setNotice(
      [
        `${filed.orderNumber}: pay application #${String(filed.applicationNumber)} is with your underwriter.`,
        estimatedReleaseLine(filed.estimatedRelease),
      ]
        .filter((line) => line !== null)
        .join(" "),
    );
    setAttempt((n) => n + 1);
    onChanged?.();
  }

  const layout =
    perBond && projects !== null
      ? aggregateSegments(projects, capacityRequest)
      : null;
  const segmented =
    layout !== null &&
    aggregateLimit !== null &&
    (layout.segments.length > 0 || layout.looseCredit !== null);
  const pct =
    aggregateLimit === null
      ? null
      : percentHeld(aggregateUtilized, aggregateLimit);

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
        <p className="text-xs uppercase tracking-wide text-brk-dim">
          Aggregate utilized
        </p>
        {segmented && (
          <p className="text-xs text-brk-dim">
            Select a segment to update that bond
          </p>
        )}
      </div>

      {segmented && layout ? (
        <SegmentedBar
          limit={aggregateLimit}
          segments={layout.segments}
          looseCredit={layout.looseCredit}
          onPick={(segment) =>
            segment.kind === "bid"
              ? setBidFor(segment)
              : segment.kind === "pp"
                ? setPayAppFor(segment.orderId)
                : undefined
          }
        />
      ) : (
        pct !== null && (
          <div
            role="progressbar"
            aria-label="Aggregate utilized"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={pct}
            className="h-1.5 w-full overflow-hidden rounded-full bg-brk-border"
          >
            <div
              className="h-full rounded-full bg-brk-accent"
              style={{ width: `${pct}%` }}
            />
          </div>
        )
      )}

      <p className="break-words text-heading text-brk-text">
        {aggregateLimit === null
          ? `${formatPreapprovalMoney(aggregateUtilized)} used`
          : `${formatPreapprovalMoney(aggregateUtilized)} of ${formatPreapprovalMoney(aggregateLimit)} used`}
      </p>
      {aggregateLimit !== null && remaining !== null && (
        <p className="text-sm text-brk-dim">
          {formatPreapprovalMoney(remaining)} available
        </p>
      )}

      {segmented && layout && <Legend layout={layout} />}

      {layout && (layout.segments.length > 0 || layout.looseCredit) && (
        <SegmentRows
          segments={layout.segments}
          looseCredit={layout.looseCredit}
          onBid={setBidFor}
          onPayApp={(segment) => setPayAppFor(segment.orderId)}
        />
      )}

      {projectsError && (
        <div className="flex flex-col items-start gap-2">
          <p role="alert" className="text-sm text-brk-danger">
            {projectsError}
          </p>
          <button
            type="button"
            onClick={() => setAttempt((n) => n + 1)}
            className={buttonClass({ variant: "secondary", size: "sm" })}
          >
            Try again
          </button>
        </div>
      )}
      {notice && (
        <p role="status" className="text-sm text-brk-text">
          {notice}
        </p>
      )}

      <button
        type="button"
        onClick={() => setOpen(true)}
        className="self-start text-sm font-medium text-brk-accent hover:underline"
      >
        View transaction history
      </button>
      <CapacityHistoryPanel
        accountId={accountId}
        open={open}
        onClose={() => setOpen(false)}
      />
      {perBond && (
        <>
          <ReportBidResultDialog
            segment={bidFor}
            onClose={() => setBidFor(null)}
            onRecorded={(segment, _sent, order) => onRecorded(segment, order)}
          />
          <PayApplicationDialog
            accountId={accountId}
            orderId={payAppFor ?? null}
            open={payAppFor !== undefined}
            onClose={() => setPayAppFor(undefined)}
            onFiled={onPayAppFiled}
          />
        </>
      )}
    </div>
  );
}

/**
 * The track: one button per segment, laid out from the served amounts against
 * the aggregate limit. Keyboard-reachable (each is a real button with the full
 * accessible name); never wider than its track at any width.
 */
function SegmentedBar({
  limit,
  segments,
  looseCredit,
  onPick,
}: {
  limit: number | null;
  segments: AggregateSegment[];
  looseCredit: LooseCredit | null;
  onPick: (segment: AggregateSegment) => void;
}) {
  const scale = layoutScale(limit, segments, looseCredit);
  return (
    <div
      role="group"
      aria-label="Aggregate utilized by bond"
      className="flex h-6 w-full min-w-0 gap-px overflow-hidden rounded-md bg-brk-border"
    >
      {segments.map((segment) => {
        const action = segmentActionLabel(segment);
        const style: CSSProperties = {
          flex: `0 1 ${String(segmentWidthPercent(segment.amount, scale))}%`,
        };
        const hatch = segment.creditInReview !== null && (
          <span
            aria-hidden="true"
            className="ml-auto block h-full"
            style={{
              ...HATCH,
              width: `${String(
                segmentWidthPercent(segment.creditInReview, segment.amount),
              )}%`,
            }}
          />
        );
        return action ? (
          <button
            key={segment.orderId}
            type="button"
            aria-label={segmentAccessibleName(segment)}
            onClick={() => onPick(segment)}
            className={`flex h-full min-w-2 p-0 ${KIND_FILL[segment.kind]} hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brk-text focus-visible:[outline-offset:-2px]`}
            style={style}
          >
            {hatch}
          </button>
        ) : (
          <span
            key={segment.orderId}
            role="img"
            aria-label={segmentAccessibleName(segment)}
            className={`flex h-full min-w-2 ${KIND_FILL[segment.kind]}`}
            style={style}
          >
            {hatch}
          </span>
        );
      })}
      {looseCredit && (
        <span
          role="img"
          aria-label={`${formatBondCurrency(looseCredit.amount)} credit in review`}
          className="block h-full min-w-2"
          style={{
            ...HATCH,
            flex: `0 1 ${String(segmentWidthPercent(looseCredit.amount, scale))}%`,
          }}
        />
      )}
    </div>
  );
}

function Legend({
  layout,
}: {
  layout: { segments: AggregateSegment[]; looseCredit: LooseCredit | null };
}) {
  const kinds = (["pp", "bid", "other"] as const).filter((k) =>
    layout.segments.some((s) => s.kind === k),
  );
  const credit =
    layout.looseCredit !== null ||
    layout.segments.some((s) => s.creditInReview !== null);
  return (
    <ul
      aria-label="Legend"
      className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-brk-dim"
    >
      {kinds.map((kind) => (
        <li key={kind} className="flex items-start gap-1.5">
          <Swatch kind={kind} />
          <span>{segmentKindLabel(kind)}</span>
        </li>
      ))}
      {credit && (
        <li className="flex items-start gap-1.5">
          <Swatch kind="credit" />
          <span>Credit in review</span>
        </li>
      )}
    </ul>
  );
}

/** A row per segment: what it is, what counts, and its action. Stacked at
 *  375; the action sits beside the text from `sm:`. */
function SegmentRows({
  segments,
  looseCredit,
  onBid,
  onPayApp,
}: {
  segments: AggregateSegment[];
  looseCredit: LooseCredit | null;
  onBid: (segment: AggregateSegment) => void;
  onPayApp: (segment: AggregateSegment) => void;
}) {
  return (
    <ul
      aria-label="Bonds holding capacity"
      className="flex flex-col divide-y divide-brk-border"
    >
      {segments.map((segment) => {
        const action = segmentActionLabel(segment);
        return (
          <li
            key={segment.orderId}
            className="flex min-w-0 flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
          >
            <div className="flex min-w-0 gap-2">
              <Swatch kind={segment.kind} />
              <div className="flex min-w-0 flex-col gap-0.5">
                <p className="break-words text-sm text-brk-text">
                  <Link
                    to={`/surety/submissions/${encodeURIComponent(segment.orderId)}`}
                    className="font-medium text-brk-accent hover:underline"
                  >
                    {segment.orderNumber}
                  </Link>{" "}
                  · {segmentKindLabel(segment.kind)} ·{" "}
                  {formatBondCurrency(segment.amount)}
                </p>
                {segment.projectName && (
                  <p className="break-words text-sm text-brk-dim">
                    {segment.projectName}
                  </p>
                )}
                <p className="text-xs text-brk-dim">{segmentNote(segment)}</p>
                {/* Where this bond's latest pay application stands
                    (BRK-S451), from its own served row — never computed. */}
                {segment.kind === "pp" &&
                  payApplicationLine(segment.payApplication) && (
                    <p className="text-xs font-medium text-brk-text">
                      {payApplicationLine(segment.payApplication)}
                    </p>
                  )}
              </div>
            </div>
            {action && (
              <button
                type="button"
                aria-label={`${action} for ${segment.orderNumber}`}
                onClick={() =>
                  segment.kind === "bid" ? onBid(segment) : onPayApp(segment)
                }
                className={`shrink-0 self-start sm:self-center ${buttonClass({ variant: "secondary", size: "sm" })}`}
              >
                {action}
              </button>
            )}
          </li>
        );
      })}
      {looseCredit && (
        <li className="flex min-w-0 flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <div className="flex min-w-0 gap-2">
            <Swatch kind="credit" />
            <div className="flex min-w-0 flex-col gap-0.5">
              <p className="text-sm text-brk-text">
                Credit in review · {formatBondCurrency(looseCredit.amount)}
              </p>
              <p className="text-xs text-brk-dim">
                A capacity request for this bond is with your underwriter.
              </p>
            </div>
          </div>
          <Link
            to={`/surety/submissions/${encodeURIComponent(looseCredit.orderId)}`}
            className="shrink-0 self-start text-sm font-medium text-brk-accent hover:underline sm:self-center"
          >
            View the bond request
          </Link>
        </li>
      )}
    </ul>
  );
}

/**
 * The drill-in: a bottom SHEET on a phone, a right-hand side PANEL from 768 px
 * (Tailwind `md:`). One component and one set of rows — only the frame moves.
 * MUI's Modal supplies the focus trap, Escape and the backdrop.
 */
function CapacityHistoryPanel({
  accountId,
  open,
  onClose,
}: {
  accountId: string;
  open: boolean;
  onClose: () => void;
}) {
  const { authedRequest } = useAuth();
  const [rows, setRows] = useState<CapacityHistoryRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setRows(null);
    setError(null);
    (async () => {
      try {
        const page = await getAccountCapacityHistory(authedRequest, accountId, {
          limit: HISTORY_PAGE_SIZE,
        });
        if (cancelled) return;
        setRows(page.items);
        setTotal(page.total);
      } catch (err) {
        // A handled line, never a throw — and never a false "no activity"
        // (NN #7).
        if (!cancelled) setError(messageFor(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, accountId, authedRequest, attempt]);

  const loadMore = useCallback(async () => {
    if (!rows) return;
    setLoadingMore(true);
    setError(null);
    try {
      const page = await getAccountCapacityHistory(authedRequest, accountId, {
        limit: HISTORY_PAGE_SIZE,
        offset: rows.length,
      });
      setRows([...rows, ...page.items]);
      setTotal(page.total);
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setLoadingMore(false);
    }
  }, [authedRequest, accountId, rows]);

  return (
    <Modal open={open} onClose={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        className="fixed inset-x-0 bottom-0 flex max-h-[85vh] w-screen max-w-[100vw] flex-col rounded-t-2xl bg-brk-panel shadow-xl outline-none md:inset-y-0 md:left-auto md:right-0 md:max-h-none md:w-[28rem] md:rounded-none"
      >
        <div className="flex items-center justify-between gap-3 border-b border-brk-border px-4 py-3 sm:px-6">
          <h2 id={TITLE_ID} className="text-heading text-brk-text">
            Transaction history
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-2 py-1 text-sm font-medium text-brk-accent hover:bg-brk-bg"
          >
            Close
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 py-3 sm:px-6">
          {rows === null && !error && (
            <p role="status" className="text-sm text-brk-dim">
              Loading history…
            </p>
          )}

          {rows !== null && rows.length === 0 && (
            <p className="text-sm text-brk-text">
              No activity on this program yet.
            </p>
          )}

          {rows !== null && rows.length > 0 && (
            <ul className="flex flex-col">
              {rows.map((row, i) => (
                <HistoryRow key={`${row.occurredAt}-${i}`} row={row} />
              ))}
            </ul>
          )}

          {error && (
            <div className="flex flex-col items-start gap-2">
              <p role="alert" className="text-sm text-brk-danger">
                {error}
              </p>
              {rows === null && (
                <button
                  type="button"
                  onClick={() => setAttempt((n) => n + 1)}
                  className="rounded-md border border-brk-border px-4 py-2 text-sm font-medium text-brk-text transition-colors hover:bg-brk-bg"
                >
                  Try again
                </button>
              )}
            </div>
          )}

          {rows !== null && rows.length < total && (
            <button
              type="button"
              onClick={() => void loadMore()}
              disabled={loadingMore}
              className="self-start rounded-md border border-brk-border px-4 py-2 text-sm font-medium text-brk-text transition-colors hover:bg-brk-bg disabled:opacity-60"
            >
              {loadingMore ? "Loading…" : "Load more"}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}

function HistoryRow({ row }: { row: CapacityHistoryRow }) {
  return (
    <li className="flex min-w-0 flex-col gap-1 border-b border-brk-border py-3 last:border-b-0">
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-medium text-brk-text">
          {capacityKindLabel(row.kind)}
        </span>
        <span className="shrink-0 font-semibold text-brk-text">
          {formatPreapprovalMoney(row.amount)}
        </span>
      </div>
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 text-sm text-brk-dim">
        <span>{formatBondDate(row.occurredAt)}</span>
        {row.sbNumber && (
          <>
            <span aria-hidden="true">·</span>
            {/* A row with no pointer id lists its number as plain text rather
                than a dead link. */}
            {row.bondSubmissionId ? (
              <Link
                to={`/surety/submissions/${encodeURIComponent(row.bondSubmissionId)}`}
                className="font-medium text-brk-accent hover:underline"
              >
                {row.sbNumber}
              </Link>
            ) : (
              <span>{row.sbNumber}</span>
            )}
          </>
        )}
        {row.projectName && (
          <>
            <span aria-hidden="true">·</span>
            <span className="min-w-0 break-words">{row.projectName}</span>
          </>
        )}
      </div>
      <p className="text-xs text-brk-dim">
        Utilized after: {formatPreapprovalMoney(row.runningUtilized)}
      </p>
    </li>
  );
}
